import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../store';
import { StageCard, sameMember, type EditableStage } from './StageBuilder';
import { resolvePaymentChain, paymentPolicyProblem } from '../payment-approval';
import type { TheWorld } from '../signoff-policy';
import type {
  PaymentApprovalPolicy,
  PaymentStage,
  SignoffStageMember,
  SignoffStageMode,
  StoredPaymentApproval,
} from '../types';
import type { PaymentChainRow } from '../projects';
import { currencyByCode } from '../money';
import { can } from '../permissions';
import Icon from './Icon';
import InfoTip from './InfoTip';
import { useSampleInert } from './SampleStrip';

/**
 * ── WHO AUTHORISES A PAYMENT — the chain builder (executive-approver tier) ─────
 *
 * The surface Vinnie approved on 4 Oct, built to the mockup
 * (dev-preview/payment-approval-ui). It edits the WORKSPACE chain the dormant
 * engine (src/payment-approval.ts) reads: an ordered list of stages, each cleared
 * by any one / a quorum / all of its signers, with the one money-specific
 * primitive — a VALUE BAND that ADDS a stage above a threshold. A workspace
 * default plus per-project overrides, mirroring the sign-off rule screen, and
 * reusing its very stage card (StageBuilder) so the two never drift.
 *
 * It writes through `setPaymentApproval` (the tenants seam, 0182). Edits persist
 * on a short debounce so a burst of typing is one saved change, not one per
 * keystroke — the audit log stays legible (audit-hardening is the lead track).
 */

/** The opinionated default the first run proposes — a sensible chain a workspace
 *  can accept as-is or edit: any one manager or the owner on every payment, and a
 *  senior approver added above a larger value. Amounts are base-currency units. */
function seedDefault(): PaymentApprovalPolicy {
  return {
    stages: [
      {
        id: crypto.randomUUID(),
        order: 0,
        label: 'Project Manager / finance',
        members: [
          { kind: 'role', role: 'manager' },
          { kind: 'role', role: 'owner' },
        ],
        mode: 'anyOne',
      },
      {
        id: crypto.randomUUID(),
        order: 1,
        label: 'Larger payments — a senior approver also signs',
        members: [{ kind: 'role', role: 'admin' }],
        mode: 'anyOne',
        band: { minAmountBase: 50000 },
      },
    ],
  };
}

export default function PaymentApproval() {
  const role = useApp((s) => s.shownRole);
  const access = useApp((s) => s.access);
  const roles = useApp((s) => s.roles);
  const projectRoles = useApp((s) => s.projectRoles);
  const profiles = useApp((s) => s.profiles);
  const projectId = useApp((s) => s.projectId);
  const meta = useApp((s) => s.meta);
  const currency = useApp((s) => s.currency);
  const projects = useApp((s) => s.projects);
  const storeVal = useApp((s) => s.paymentApproval);
  const setPaymentApproval = useApp((s) => s.setPaymentApproval);

  const canEdit = can(role, 'editSettings');
  const inert = useSampleInert();
  const code = currency || 'GBP';

  const world: TheWorld = useMemo(
    () => ({
      owner: {
        name: (meta.projectOwnerName ?? '').trim(),
        position: (meta.projectOwnerPosition ?? '').trim(),
        email: (meta.projectOwnerEmail ?? '').trim(),
      },
      access,
      roles,
      projectRoles,
      profiles,
      projectId,
    }),
    [meta, access, roles, projectRoles, profiles, projectId],
  );

  /* The edited copy. Seeded from the store, or the opinionated default when the
     workspace has never set one. A real saved default flips `saved` true, which the
     status pill and the first-run primary read. */
  const [draft, setDraft] = useState<StoredPaymentApproval>(() => storeVal ?? { default: seedDefault() });
  const saved = !!storeVal?.default;
  const touched = useRef(false);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  /* Pick up a store value that arrives after mount (async board load, or a save on
     another screen) — but never clobber edits in flight. */
  useEffect(() => {
    if (!touched.current && storeVal) setDraft(storeVal);
  }, [storeVal]);

  /* Persist a burst of edits as one change. */
  useEffect(() => {
    if (!touched.current) return;
    const t = setTimeout(() => void setPaymentApproval(draftRef.current), 600);
    return () => clearTimeout(t);
  }, [draft, setPaymentApproval]);

  /* Don't lose an edit that was still within the debounce when the screen closes. */
  useEffect(
    () => () => {
      if (touched.current) void useApp.getState().setPaymentApproval(draftRef.current);
    },
    [],
  );

  const edit = (next: StoredPaymentApproval) => {
    touched.current = true;
    setDraft(next);
  };

  const defaultStages = draft.default?.stages ?? [];
  const setDefaultStages = (next: PaymentStage[]) =>
    edit({ ...draft, default: { stages: next.map((s, i) => ({ ...s, order: i })) } });

  const overrides = draft.overrides ?? {};
  const setOverrideStages = (pid: string, next: PaymentStage[]) =>
    edit({ ...draft, overrides: { ...overrides, [pid]: { stages: next.map((s, i) => ({ ...s, order: i })) } } });
  const addOverride = (pid: string) => {
    const from = draft.default?.stages ?? [];
    const copy: PaymentStage[] = from.length
      ? from.map((s, i) => ({ ...s, id: crypto.randomUUID(), order: i, members: s.members.map((m) => ({ ...m })) }))
      : [{ id: crypto.randomUUID(), order: 0, label: '', members: [], mode: 'all' as SignoffStageMode }];
    edit({ ...draft, overrides: { ...overrides, [pid]: { stages: copy } } });
    setOpenOverride(pid);
  };
  const resetOverride = (pid: string) => {
    const { [pid]: _drop, ...rest } = overrides;
    void _drop;
    edit({ ...draft, overrides: rest });
    if (openOverride === pid) setOpenOverride(null);
  };

  const [openOverride, setOpenOverride] = useState<string | null>(null);

  const defaultPolicy: PaymentApprovalPolicy = { stages: defaultStages };
  const problem = defaultStages.length ? paymentPolicyProblem(defaultPolicy, world) : '';

  return (
    <div className="wrap">
      <div className="fd-form">
        <div className="fd-head">
          <div className="fd-head-main">
            <h1 className="fd-title">Payment Approval</h1>
            <p className="fd-sub">
              Who authorises a payment, and above what value. Every payment on this workspace passes this chain before
              it can be paid.
            </p>
          </div>
          <div className="fd-head-side">
            {saved ? (
              <span className="rag green">
                <Icon name="check" size={14} /> Chain set
              </span>
            ) : (
              <span className="rag amber">Not saved yet</span>
            )}
          </div>
        </div>

        {/* ── THE STAGES ─────────────────────────────────────────────────────── */}
        <section className="fd-sec sr-block">
          <h2 className="fd-sec-h">
            The stages, in the order a payment is authorised{' '}
            <InfoTip label="the stages">
              Each stage is asked only once the stage before it has authorised. A stage can ask a role, a person on this
              board, or someone outside it by email — and it can apply to every payment, or only above a value.
            </InfoTip>
          </h2>
          <p className="sr-note">
            A stage can ask a role (worked out fresh each time), a person on this board, or someone outside it by email
            — and it can apply to every payment, or only above a value. A value band <b>adds</b> a signer above a
            threshold; it never removes the stages below it.
          </p>

          <div className="sr-default-bar">
            <span className="sr-default-tag">Workspace default</span>
            <span className="sr-default-why">
              {saved
                ? 'Every project uses this unless you give it its own stages below.'
                : "We've set a sensible default. Review it, then save it — or just change anything and it saves."}
            </span>
          </div>

          <PaymentStageList
            stages={defaultStages}
            world={world}
            profiles={profiles}
            canEdit={canEdit}
            code={code}
            onChange={setDefaultStages}
          />

          {canEdit && (
            <button
              className="btn btn-small sr-add-stage"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => setDefaultStages([...defaultStages, { id: crypto.randomUUID(), order: defaultStages.length, label: '', members: [], mode: 'all' }])}
            >
              <Icon name="plus" size={16} /> Add a stage
            </button>
          )}

          {!saved && canEdit && (
            <button style={{ alignSelf: 'flex-start' }} {...inert('btn btn-primary', () => void setPaymentApproval(draftRef.current))}>
              <Icon name="check" size={15} /> Save this chain
            </button>
          )}

          {problem && (
            <p className="sr-note problem" role="status">
              {problem}
            </p>
          )}

          <div className="guard">
            <Icon name="shield" size={16} />
            <span>
              Admins <b>set up</b> this chain but are not automatically approvers — only the people named above can
              authorise a payment. At every stage, <b>whoever accepted the work is skipped</b> — FlightDeck asks the next
              eligible signer instead (separation of duties).
            </span>
          </div>
        </section>

        {/* ── WHO THIS ASKS, IN ORDER ────────────────────────────────────────── */}
        {defaultStages.length > 0 && (
          <WhoInOrder policy={defaultPolicy} world={world} code={code} />
        )}

        {/* ── BY PROJECT ─────────────────────────────────────────────────────── */}
        <section className="fd-sec sr-block">
          <h2 className="fd-sec-h">
            By project{' '}
            <InfoTip label="by project">
              Every project inherits the workspace default. Override a project only when it authorises payments
              differently — a higher-risk programme that brings the executives in sooner, say.
            </InfoTip>
          </h2>
          <p className="sr-note">
            Every project inherits the workspace default above. Override a project only when it authorises payments
            differently. Only the ones you override stop tracking the default.
          </p>
          <div className="sr-type-list">
            {projects.length === 0 && <p className="sr-note">No projects on this workspace yet.</p>}
            {projects.map((p) => {
              const ov = overrides[p.id];
              const open = openOverride === p.id;
              return (
                <div key={p.id} className={`sr-type ${ov ? 'is-override' : ''} ${open ? 'is-open' : ''}`}>
                  <div className="sr-type-head">
                    <span className="sr-type-name">{p.name}</span>
                    {ov ? (
                      <span className="sr-type-pill sr-type-own">Its own stages</span>
                    ) : (
                      <span className="sr-type-pill sr-type-inherit">Follows the default</span>
                    )}
                    {ov && !open && ov.stages.length > 0 && (
                      <span className="sr-type-chain">{ov.stages.map((s) => s.label || 'Unnamed').join(' → ')}</span>
                    )}
                    {canEdit && (
                      <span className="sr-type-actions">
                        {!ov ? (
                          <button {...inert('link', () => addOverride(p.id))}>
                            Override for this project
                          </button>
                        ) : open ? (
                          <button {...inert('link', () => resetOverride(p.id))}>
                            Reset to default
                          </button>
                        ) : (
                          <>
                            <button {...inert('link', () => setOpenOverride(p.id))}>
                              Edit
                            </button>{' '}
                            · <button {...inert('link', () => resetOverride(p.id))}>
                              Reset
                            </button>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                  {ov && open && (
                    <div className="sr-type-editor">
                      <p className="sr-type-editor-head grp-heading">{p.name} authorises a payment as</p>
                      <PaymentStageList
                        stages={ov.stages}
                        world={world}
                        profiles={profiles}
                        canEdit={canEdit}
                        code={code}
                        onChange={(next) => setOverrideStages(p.id, next)}
                      />
                      {canEdit && (
                        <button
                          className="btn btn-small sr-add-stage"
                          onClick={() => setOverrideStages(p.id, [...ov.stages, { id: crypto.randomUUID(), order: ov.stages.length, label: '', members: [], mode: 'all' }])}
                        >
                          <Icon name="plus" size={16} /> Add a stage
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

const money = (code: string, n: number) => `${currencyByCode(code).symbol}${n.toLocaleString()}`;

/* ── THE STAGE LIST — reuses the sign-off StageCard, adds the value band ──────── */
function PaymentStageList({
  stages,
  world,
  profiles,
  canEdit,
  code,
  onChange,
}: {
  stages: PaymentStage[];
  world: TheWorld;
  profiles: TheWorld['profiles'];
  canEdit: boolean;
  code: string;
  onChange: (next: PaymentStage[]) => void;
}) {
  const [dragAt, setDragAt] = useState<number | null>(null);

  const patch = (id: string, change: Partial<PaymentStage>) =>
    onChange(stages.map((s) => (s.id === id ? { ...s, ...change } : s)));
  /* Clear a band by DROPPING the key, never leaving it set to an undefined value
     — an undefined sent to the jsonb column would throw (the gate forbids it). */
  const clearBand = (id: string) =>
    onChange(
      stages.map((s) => {
        if (s.id !== id) return s;
        const { band: _b, ...rest } = s;
        void _b;
        return rest;
      }),
    );
  const removeStage = (id: string) => onChange(stages.filter((s) => s.id !== id));
  const move = (at: number, dir: -1 | 1) => {
    const to = at + dir;
    if (to < 0 || to >= stages.length) return;
    const next = [...stages];
    [next[at], next[to]] = [next[to], next[at]];
    onChange(next);
  };
  const drop = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0) return;
    const next = [...stages];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };
  const addMember = (id: string, m: SignoffStageMember) => {
    const st = stages.find((s) => s.id === id);
    if (!st || st.members.some((x) => sameMember(x, m))) return;
    patch(id, { members: [...st.members, m] });
  };
  const removeMember = (id: string, at: number) => {
    const st = stages.find((s) => s.id === id);
    if (!st) return;
    const members = st.members.filter((_, i) => i !== at);
    const next: Partial<PaymentStage> = { members };
    if (typeof st.mode === 'object' && (members.length < 3 || st.mode.quorum > members.length - 1)) next.mode = 'all';
    patch(id, next);
  };

  return (
    <div className="sr-stage-list">
      {stages.map((stage, at) => {
        const n = stage.members.length;
        const count = `${n} ${n === 1 ? 'person' : 'people'}`;
        const quorum = typeof stage.mode === 'object' ? stage.mode.quorum : null;
        const sel: 'all' | 'anyOne' | 'quorum' = stage.mode === 'anyOne' ? 'anyOne' : quorum != null ? 'quorum' : 'all';
        const qShown = Math.min(Math.max(quorum ?? 2, 2), Math.max(2, n - 1));
        const who =
          n === 0
            ? 'No signers yet'
            : sel === 'anyOne'
              ? `Any one of ${count} authorises`
              : sel === 'quorum'
                ? `At least ${qShown} of ${count} authorise`
                : `Everyone here authorises (${count})`;
        const applies = stage.band
          ? `Asked only when a payment is ${money(code, stage.band.minAmountBase)} or more. It is added above the earlier stage, never instead of it.`
          : 'Applies to every payment.';

        return (
          <StageCard
            key={stage.id}
            stage={stage as EditableStage}
            at={at}
            total={stages.length}
            world={world}
            profiles={profiles}
            canSettings={canEdit}
            dragging={dragAt === at}
            onDragStart={() => setDragAt(at)}
            onDragEnd={() => setDragAt(null)}
            onDropHere={() => {
              if (dragAt !== null) drop(dragAt, at);
              setDragAt(null);
            }}
            onRename={(label) => patch(stage.id, { label })}
            onRemove={() => removeStage(stage.id)}
            onMove={(dir) => move(at, dir)}
            onAddMember={(m) => addMember(stage.id, m)}
            onRemoveMember={(i) => removeMember(stage.id, i)}
            onMode={(mode) => patch(stage.id, { mode })}
            extra={
              <div className="sr-band">
                <span className="sr-toggle-name">
                  Applies to{' '}
                  <InfoTip label="applies to">
                    A stage can apply to every payment, or only to payments at or above a value — the band adds this
                    stage above the threshold without removing the stages below it.
                  </InfoTip>
                </span>
                <div className="sr-band-row">
                  <div className="seg" role="group" aria-label="Which payments this stage applies to">
                    <button
                      type="button"
                      className={`seg-btn ${!stage.band ? 'active' : ''}`}
                      aria-pressed={!stage.band}
                      disabled={!canEdit}
                      onClick={() => clearBand(stage.id)}
                    >
                      All payments
                    </button>
                    <button
                      type="button"
                      className={`seg-btn ${stage.band ? 'active' : ''}`}
                      aria-pressed={!!stage.band}
                      disabled={!canEdit}
                      onClick={() => patch(stage.id, { band: { minAmountBase: stage.band?.minAmountBase || 50000 } })}
                    >
                      Above…
                    </button>
                  </div>
                  {stage.band && (
                    <BandAmount
                      value={stage.band.minAmountBase}
                      code={code}
                      canEdit={canEdit}
                      onCommit={(v) => patch(stage.id, { band: { minAmountBase: v } })}
                    />
                  )}
                </div>
                {stage.band && (
                  <p className="sr-toggle-why">
                    Compared on the invoice's headline amount in {code} (base) — a foreign invoice is converted before
                    the band is checked.
                  </p>
                )}
              </div>
            }
            summary={
              <p className="sr-note sr-stage-sum">
                {n === 0 ? 'No signers yet.' : `${who}. ${applies}`}
              </p>
            }
          />
        );
      })}
    </div>
  );
}

/** The band threshold input — own text state so typing isn't re-formatted mid-edit;
 *  commits the parsed whole-unit amount on blur or Enter. */
function BandAmount({
  value,
  code,
  canEdit,
  onCommit,
}: {
  value: number;
  code: string;
  canEdit: boolean;
  onCommit: (v: number) => void;
}) {
  const [txt, setTxt] = useState(value.toLocaleString());
  useEffect(() => {
    setTxt(value.toLocaleString());
  }, [value]);
  const commit = (raw: string) => {
    const n = parseInt(raw.replace(/[^\d]/g, ''), 10);
    onCommit(Number.isFinite(n) && n > 0 ? n : 0);
  };
  return (
    <span className="sr-amount">
      <span className="cur">
        {code}&nbsp;{currencyByCode(code).symbol}
      </span>
      <input
        value={txt}
        disabled={!canEdit}
        inputMode="numeric"
        aria-label="Band threshold amount"
        onChange={(e) => setTxt(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
        }}
      />
    </span>
  );
}

/* ── WHO THIS AUTHORISES, IN ORDER — read through the engine, never recomputed ── */
function WhoInOrder({ policy, world, code }: { policy: PaymentApprovalPolicy; world: TheWorld; code: string }) {
  // Resolve against an amount above every band, so the preview shows the whole
  // chain; each group then states its own band so the reader sees when it applies.
  const rows = useMemo(() => resolvePaymentChain(policy, Number.MAX_SAFE_INTEGER, world), [policy, world]);
  const labelOf = (step: string) => policy.stages.find((s) => s.id === step)?.label || 'Unnamed stage';

  const groups: { step: string; rows: PaymentChainRow[]; mode?: SignoffStageMode; band?: number }[] = [];
  for (const r of rows) {
    let g = groups.find((x) => x.step === r.step);
    if (!g) {
      g = { step: r.step, rows: [], mode: r.stageMode, band: r.band };
      groups.push(g);
    }
    g.rows.push(r);
  }

  const modeText = (g: { rows: PaymentChainRow[]; mode?: SignoffStageMode }) => {
    if (g.mode === 'anyOne') return 'any one of them';
    if (g.mode && typeof g.mode === 'object') return `at least ${Math.min(g.mode.quorum, g.rows.length)} of them`;
    return 'everyone';
  };

  if (groups.length === 0) return null;

  return (
    <section className="fd-sec sr-block">
      <h2 className="fd-sec-h">Who this authorises, in order</h2>
      <div className="sr-who">
        {groups.map((g) => (
          <div className="sr-who-step" key={g.step}>
            <span className="grp-heading">
              {labelOf(g.step)}
              <span className="sr-who-mode">
                {' '}
                — {modeText(g)} · {g.band != null ? `above ${money(code, g.band)}` : 'all payments'}
              </span>
            </span>
            <ol className="sr-who-list">
              {g.rows.map((r, i) => (
                <li key={`${g.step}-${i}`}>
                  <strong>{r.who.name || 'Unnamed'}</strong>
                  {r.who.position ? ` — ${r.who.position}` : ''}
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </section>
  );
}
